import isNil from 'lodash/isNil';
import moment from 'moment';

import { DATE_TIME_FORMAT, ISO_TIME_FORMAT, TIME_24_FORMAT } from 'src/shared/constants';

/**
 * Converts user input into ISO time string
 * @param input User time input string
 * @param mode
 * @returns ISO time string if user input is valid or empty string if invalid
 */
export const convertUserInputToTime = (input: string, mode: 'am' | 'pm' | '24h' = '24h') => {
  const sInput = input.toLowerCase().replace(/\s/g, '').split('');
  const time = {
    h: '',
    m: '',
  };
  let key: 'h' | 'm' = 'h';
  let format = 'iso';
  let error = false;
  sInput.every((char) => {
    if (char === ':' && key !== 'm') {
      key = 'm';
      format = 'iso';
      return true;
    }
    if (char === '.' && key !== 'm') {
      key = 'm';
      format = 'decimal';
      return true;
    }
    if (/\d/.test(char)) {
      time[key] += char;
      return true;
    }
    if (char === 'a') {
      mode = 'am';
      return false;
    }
    if (char === 'p') {
      mode = 'pm';
      return false;
    }
    return false;
  });

  // Invalid if first character is not a number, colon or period
  if (!time.h && !time.m) {
    error = true;
  }

  if (time.h.length > 2 && !time.m) {
    time.h = time.h.substring(0, 4);
    time.m = time.h.slice(-2);
    time.h = time.h.slice(0, -2).padStart(2, '0');
  } else {
    time.h = time.h.padStart(2, '0');
    time.m = time.m.slice(0, 2).padEnd(2, '0');
  }

  if (format === 'decimal') {
    time.m = ((+time.m * 60) / 100).toFixed().toString().padStart(2, '0');
  }

  if (mode === 'am' || mode === 'pm') {
    // Invalid if hour > 12 in "am" or "pm" mode
    if (+time.h > 12) error = true;
    if (mode === 'pm' && +time.h < 12) {
      time.h = (+time.h + 12).toString().padStart(2, '0');
    }
  }

  // Moment considers "24:00:00" as a valid time; max valid input time should be "23:59:00"
  if (+time.h >= 24) {
    error = true;
  }

  const formattedTime = `${time.h}:${time.m}:00`;

  // Invalid if not valid ISO time
  if (!moment(formattedTime, 'HH:mm:ss').isValid()) {
    error = true;
  }

  return error ? '' : formattedTime;
};

const DURATION_23H59M_IN_SECONDS = 60 * 60 * 24 - 60;

export const convertUserInputToDuration = (
  input: string,
  isNegativeAllowed = false,
  max: number | null = DURATION_23H59M_IN_SECONDS,
) => {
  const isNegative = isNegativeAllowed && input[0] === '-';
  const inputStringArray = input
    .substring(isNegative ? 1 : 0)
    .toLowerCase()
    .replace(/,/g, '')
    .replace(/\s/g, '')
    .split('');

  const duration = { h: '', m: '' };
  let key: 'h' | 'm' = 'h';
  let format = 'iso';
  let error = false;

  for (const char of inputStringArray) {
    if (char === ':' && key !== 'm') {
      key = 'm';
      format = 'iso';
      continue;
    }

    if (char === 'h' && key !== 'm') {
      key = 'm';
      format = 'iso';
      continue;
    }

    if (char === '.' && key !== 'm') {
      key = 'm';
      format = 'decimal';
      continue;
    }

    if (/\d/.test(char)) {
      duration[key] += char;
      continue;
    }

    break;
  }

  // Invalid if first character is not a number, colon or period
  if (!duration.h && !duration.m) {
    error = true;
  }

  if (max === null) {
    if (key === 'h') {
      duration.h = duration.h.substring(0, duration.h.length);
    } else {
      duration.h = duration.h.padStart(2, '0');
      duration.m = duration.m.slice(0, 2).padEnd(2, '0');
    }
  } else {
    const maxLength = Math.trunc(max / 60 / 60).toString().length;

    if (duration.h.length > maxLength && !duration.m) {
      duration.h = duration.h.substring(0, maxLength + 2);
      duration.m = duration.h.slice(-maxLength);
      duration.h = duration.h.slice(0, -maxLength).padStart(maxLength, '0');
    } else {
      duration.h = duration.h.padStart(maxLength, '0');
      duration.m = duration.m.slice(0, maxLength).padEnd(maxLength, '0');
    }
  }

  if (format === 'decimal') {
    duration.m = ((+duration.m * 60) / 100).toFixed().toString().padStart(2, '0');
  }

  if (error) {
    return '';
  }

  const valueInSeconds = moment.duration({ minutes: +duration.m, hours: +duration.h }).asSeconds();
  const minValue = max ? Math.min(valueInSeconds, max) : valueInSeconds;

  return (isNegative ? -1 : 1) * minValue;
};

export const isValidUserTimeInput = (value: string) => {
  return !value || !!convertUserInputToTime(value);
};

export const getTimeFromApiDateTime = (value: string) => {
  return moment(value, DATE_TIME_FORMAT).format(ISO_TIME_FORMAT);
};

export const getTimeFromSeconds = (
  seconds: number | null | undefined,
  timeFormat: string = TIME_24_FORMAT,
): string | null => {
  return !isNil(seconds) ? moment.utc(seconds * 1000).format(timeFormat) : null;
};

export const getSecondsFromTime = (
  time: string | moment.Moment | null | undefined,
  timeFormat: string = TIME_24_FORMAT,
): number | null => {
  return !isNil(time) ? moment(time, timeFormat).diff(moment().startOf('day'), 'seconds') : null;
};
